import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Page from '@pelckmans/elementis/components/page';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import ContentBody from '../../../layout/content/ContentBody';
import ContentHeader from '../../../layout/content/ContentHeader';
import AggregatedModuleAlerts from '../../../shared-components/alerts/AggregatedModuleAlerts';
import {
  withScrollThreshold,
  type WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import ModuleContext from '../_contexts/ModuleContext';
import SuperModuleBreadcrumb from '../components/breadcrumbs/SuperModuleBreadcrumb';
import AdvancedSearchField from './components/AdvancedSearchField';
import SearchResultList from './components/SearchResultList';
import { SearchTab } from './api/search-api';
import { useSearchWithOffset } from './hooks/useSearchWithOffset';
import { useSearchFacets } from './hooks/useSearchFacets';

const TABS = {
  ALL: 'all',
  MATERIAL: 'material',
  TOC: 'toc',
  SLIDESET: 'slideset',
  DIGIBOOK: 'digibook',
} as const;

const TABS_ARRAY = Object.values(TABS);

function Search({ pastThreshold }: WithScrollThresholdProps) {
  const { t } = useTranslation();
  useDocumentTitle(t('PAGES.SEARCH.DOCUMENT_TITLE'));

  const { module, allModules } = useContext(ModuleContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get('q') || '';
  const tabParam = searchParams.get('tab') || TABS.ALL;

  const selectedTab: SearchTab = TABS_ARRAY.includes(tabParam as SearchTab) ? (tabParam as SearchTab) : TABS.ALL;

  const { results, isLoading, isLoadingMore, hasMore, loadMore } = useSearchWithOffset(
    module?.id,
    searchTerm,
    selectedTab,
  );
  const { facets } = useSearchFacets(module?.id, searchTerm);

  const handleTabChange = useCallback(
    (tab: SearchTab) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('tab', tab);
        return newParams;
      });
    },
    [setSearchParams],
  );

  const selectedModules = useMemo(() => {
    if (!results?.length) return [];
    const ids = new Set(results.map(r => r.moduleId));

    return allModules.filter(m => ids.has(m.id));
  }, [results, allModules]);

  return (
    <>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={t('PAGES.SEARCH.DOCUMENT_TITLE')}
            breadcrumbs={
              <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                <SuperModuleBreadcrumb />
              </Breadcrumbs>
            }
            extra={<AdvancedSearchField />}
          />
        }
      />
      <ContentBody alerts={<AggregatedModuleAlerts selectedModules={selectedModules} />}>
        <SearchResultList
          results={results}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          facets={facets}
        />
      </ContentBody>
    </>
  );
}

export default withScrollThreshold(Search);
